import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import CountrySelector from '../components/CountrySelector'
import { doE164, formatPhoneNumber, getCallMode } from "../components/Utils";
import NumberPad from "../components/NumberPad";
import { ReactSipPhone, phoneStore } from "react-sip-phone";
import { isEmpty } from "lodash";
import "react-sip-phone/dist/index.css";
import "../Dialler/DiallerKeyPad.css";
import { useSelector, useDispatch } from "react-redux";
import { resetCallInfo } from '../../../state/actions/phone'
import Config from "../../../utils/config/config";
import countryCodes from "../components/CountryList";
import { fetchWalletBalanceService, getLoggedInUserCallHistory } from "../../../state/actions/user";
import { UserServiceClientApi } from "../../../services/api/UserApiServices";
import { RechargeContext } from "../../../RechargeContext";
import { showToast } from "../../../components/ui/toaster";
import webSocketApiService from "../../../services/socket/wsApiServices";

const sipuri = Config.sipURI;
const password = Config.sipPassword;
const websocket = Config.sipWebsocket;
const name = "";
const disabledButtons = [
	"numpad",
	"transfer",
];
const disabledFeatures = ["dialstring", "settings", "remoteid"];
const mode = "";

const DiallerKeyPad = ({
	callerInfo,
	phoneNumFromLink,
	onClose,
	noBalance,
}) => {
	const [country, setCountry] = useState(null);
	const [fullPhoneNumber, setFullPhoneNumber] = useState("+");
	const [phoneState, setPhoneState] = useState(null);
	const [duration, setDuration] = useState(0);
	const [showNumberPlate, setShowNumberPlate] = useState(true);
	const [callTriggered, setCallTriggered] = useState(false);
	const [callStartTime, setCallStartTime] = useState(null);
	const [isInitialized, setIsInitialized] = useState(false);
	const reduxState = useSelector(state => state.user)
	const { openRecharge } = useContext(RechargeContext);

	const user = useSelector((state) => state.user);
	const selectedDomain = useSelector((state) => state.domain.selectedDomain);
	const dispatch = useDispatch();
	const phnInput = useRef(null);
	const cursor = useRef(0);
	const phoneStateInterval = useRef(null);

	// Add refs to track API call states to prevent duplicates
	const apiCallStates = useRef({
		callStartSent: false,
		callEndSent: false,
		callSummarySent: false,
		lastProcessedState: null,
		callStartTimestamp: null,
		callDurationStart: null
	});

	const setCursorPositions = useCallback(() => {
		// Use setTimeout to ensure DOM is ready
		setTimeout(() => {
			if (phnInput.current) {
				try {
					phnInput.current.setSelectionRange(cursor.current, cursor.current);
				} catch (error) {
					console.warn('Could not set cursor position:', error);
				}
			}
		}, 0);
	}, []);

	// Helper function to find country by dial code
	const findCountryByDialCode = useCallback((number) => {
		if (!number || number.length < 2) return null;

		try {
			// Sort countries by dial code length (longest first) to match most specific codes
			const sortedCountries = [...countryCodes].sort((a, b) => b.dial_code.length - a.dial_code.length);

			for (let country of sortedCountries) {
				if (number.startsWith(country.dial_code)) {
					return country;
				}
			}
		} catch (error) {
			console.error('Error finding country by dial code:', error);
		}
		return null;
	}, []);

	// Helper function to clean and validate phone number input
	const cleanPhoneNumber = useCallback((input) => {
		try {
			// Remove all non-numeric characters except +
			let cleaned = input.replace(/[^\d+]/g, '');

			// Ensure it starts with +
			if (!cleaned.startsWith('+')) {
				if (cleaned.length > 0) {
					cleaned = '+' + cleaned;
				} else {
					cleaned = '+';
				}
			}

			// Remove any + that are not at the beginning
			cleaned = '+' + cleaned.substring(1).replace(/\+/g, '');

			return cleaned;
		} catch (error) {
			console.error('Error cleaning phone number:', error);
			return '+';
		}
	}, []);

	const selectCountry = useCallback((selectedCountry) => {
		try {
			setCountry(selectedCountry);
			const formattedNumber = formatPhoneNumber(selectedCountry.dial_code, null);
			setFullPhoneNumber(formattedNumber);
			cursor.current = selectedCountry.dial_code.length;
		} catch (error) {
			console.error('Error selecting country:', error);
		}
	}, []);

	const addNumber = useCallback((phoneNumber) => {
		try {
			let newNumber;

			if (fullPhoneNumber === "+") {
				newNumber = "+" + phoneNumber;
			} else {
				// Insert number at cursor position
				const beforeCursor = fullPhoneNumber.substring(0, cursor.current);
				const afterCursor = fullPhoneNumber.substring(cursor.current);
				newNumber = beforeCursor + phoneNumber + afterCursor;
			}

			// Clean the number
			newNumber = cleanPhoneNumber(newNumber);

			// Find and set country
			const foundCountry = findCountryByDialCode(newNumber);
			if (foundCountry && foundCountry !== country) {
				setCountry(foundCountry);
			}

			// Format and set the number
			const formattedNumber = formatPhoneNumber(null, newNumber);
			setFullPhoneNumber(formattedNumber);

			// Update cursor position
			cursor.current = Math.min(cursor.current + 1, formattedNumber.length);
		} catch (error) {
			console.error('Error adding number:', error);
		}
	}, [fullPhoneNumber, country, cleanPhoneNumber, findCountryByDialCode]);

	const handlePhoneNumberChange = useCallback((e) => {
		try {
			let val = e.target.value;
			const cursorPosition = e.target.selectionStart;
			cursor.current = cursorPosition;

			// Clean the input
			const cleanedVal = cleanPhoneNumber(val);

			// If user deleted everything, reset to +
			if (cleanedVal.length <= 1) {
				setFullPhoneNumber("+");
				setCountry(null);
				cursor.current = 1;
				return;
			}

			// Find country based on the cleaned number
			const foundCountry = findCountryByDialCode(cleanedVal);
			if (foundCountry && foundCountry !== country) {
				setCountry(foundCountry);
			} else if (!foundCountry && country) {
				// If no country found but we had one before, keep it if the number still starts with its dial code
				if (!cleanedVal.startsWith(country.dial_code)) {
					setCountry(null);
				}
			}

			// Format and set the number
			const formattedNumber = formatPhoneNumber(null, cleanedVal);
			setFullPhoneNumber(formattedNumber);
		} catch (error) {
			console.error('Error handling phone number change:', error);
		}
	}, [cleanPhoneNumber, findCountryByDialCode, country]);

	// Handle backspace/delete more intelligently
	const handleKeyDown = useCallback((e) => {
		try {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				const cursorPosition = e.target.selectionStart;
				cursor.current = cursorPosition;

				// If we're at position 1 (right after +), prevent deletion
				if (cursorPosition <= 1 && e.key === 'Backspace') {
					e.preventDefault();
					return;
				}
			}
		} catch (error) {
			console.error('Error handling key down:', error);
		}
	}, []);

	const resetCallStates = useCallback(() => {
		try {
			setPhoneState(null);
			setDuration(0);
			setCallStartTime(null);
			setFullPhoneNumber("+");
			setCountry(null);
			setShowNumberPlate(true);
			setCallTriggered(false);

			// Reset API call tracking
			apiCallStates.current = {
				callStartSent: false,
				callEndSent: false,
				callSummarySent: false,
				lastProcessedState: null,
				callStartTimestamp: null,
				callDurationStart: null
			};

			// Clear interval if exists
			if (phoneStateInterval.current) {
				clearInterval(phoneStateInterval.current);
				phoneStateInterval.current = null;
			}

			// Dispatch reset action
			dispatch(resetCallInfo());
		} catch (error) {
			console.error('Error resetting call states:', error);
		}
	}, [dispatch]);

	// Improved monitor phone state function with duplicate prevention
	const monitorPhoneState = useCallback(async () => {
		try {
			const phoneStoreState = phoneStore.getState();
			const sessions = phoneStoreState?.sipSessions?.sessions;

			if (!isEmpty(sessions)) {
				const sessionKey = Object.keys(sessions)[0];
				const session = sessions[sessionKey];
				const currentState = session?._state;

				// Only process if state actually changed and we haven't processed this state already
				if (currentState &&
					currentState !== phoneState &&
					currentState !== apiCallStates.current.lastProcessedState) {
					setPhoneState(currentState);
					apiCallStates.current.lastProcessedState = currentState;
					const num = (callerInfo?.toNumber || fullPhoneNumber) || "";
					// Handle state-specific logic
					const number = doE164(num);
					if (number && user?.user) {
						const callObject = {
							userId: number?.formatted?.substring(1),
							callerUserId: user.user.userId,
							videoSessionId: null,
							video: false,
							selectedDomain: selectedDomain
						};


						switch (currentState) {
							case "Established":
								// Only send CallStart if not already sent
								if (!apiCallStates.current.callStartSent) {
									webSocketApiService.SendVoipPushNotification({
										...callObject,
										callAction: "CallStart",
									});

									// Mark as sent and record timestamps
									apiCallStates.current.callStartSent = true;
									apiCallStates.current.callEndSent = false;
									apiCallStates.current.callStartTimestamp = Date.now();
									apiCallStates.current.callDurationStart = performance.now();

									setDuration(performance.now());
									setCallStartTime(Date.now());
									setShowNumberPlate(true);
								}
								break;

							case "Terminated":
								const isSatCall = getCallMode(number.countryCode) === "sat";
								setCallTriggered(false);

								// Only send CallEnd and CallSummary if not already sent and CallStart was sent
								if (
									!apiCallStates.current.callEndSent &&
									!apiCallStates.current.callSummarySent) {

									// Send CallEnd

									webSocketApiService.SendVoipPushNotification({
										...callObject,
										callAction: "CallEnd",
									});
									apiCallStates.current.callEndSent = true;

									// Send CallSummary
									const callDuration = apiCallStates.current.callDurationStart
										? (performance.now() - apiCallStates.current.callDurationStart) / 1000
										: 0;

									const callSummaryResponse = await webSocketApiService.SendVoipPushNotification({
										...callObject,
										callAction: "CallSummary",
										callStartTime: apiCallStates.current.callStartTimestamp || callStartTime,
										callDuration: callDuration,
										callType: isSatCall ? "SAT" : "PSTN",
										calledNumber: isSatCall ? "+12124010649" : number.formatted,
										dialledSatPhoneNumber: isSatCall ? number.formatted : "",
										isLocalContact: false,
										showNumberPlate: true,
									});
									const responseData = callSummaryResponse;
									if (responseData.success) {
										let now = Date.now();
										dispatch(getLoggedInUserCallHistory(now))
										dispatch(fetchWalletBalanceService())
										apiCallStates.current.callSummarySent = true;
									}
								}

								// Clear the monitoring interval since call ended
								if (phoneStateInterval.current) {
									clearInterval(phoneStateInterval.current);
									phoneStateInterval.current = null;
								}

								// Reset after a delay to allow UI updates
								setTimeout(() => {
									resetCallStates();
								}, 1000);
								break;

							default:
								break;
						}
					}
				}
			} else if (phoneState !== null) {
				// No sessions but we had a phone state - reset
				resetCallStates();
			}
		} catch (error) {
			console.error('Error monitoring phone state:', error);
		}
	}, [phoneState, fullPhoneNumber, user, callStartTime, selectedDomain, resetCallStates]);

	const makeCall = useCallback((phoneNumberToCall = null) => {
		if (callTriggered) return;

		try {
			// Use passed number or fallback to state
			let numberToUse = phoneNumberToCall || fullPhoneNumber;
			console.log('Making call with number:', numberToUse);

			// Add safety check for valid phone number
			if (!numberToUse || numberToUse === "+") {
				console.error('Cannot make call: phone number not set properly');
				return;
			}
			const number = doE164(numberToUse);
			console.log('Formatted number for call:', number, user.user?.isPostpaidUser, user?.balance);
			if (!user?.user?.isPostpaidUser) {
				if (user?.balance <= 0) {
					onClose();
					noBalance();
					return;
				}
			}

			if (number && number.formatted) {
				const phoneStoreState = phoneStore.getState();
				const sipAccount = phoneStoreState?.sipAccounts?.sipAccount;
				console.log('sipAccount', sipAccount);
				if (sipAccount) {
					console.log('Making call to:', number.formatted.substring(1));
					setCallTriggered(true);

					// Reset API call states before making new call
					apiCallStates.current = {
						callStartSent: false,
						callEndSent: false,
						callSummarySent: false,
						lastProcessedState: null,
						callStartTimestamp: null,
						callDurationStart: null
					};

					sipAccount.makeCall(number.formatted.substring(1));

					// Start monitoring phone state - clear any existing interval first
					if (phoneStateInterval.current) {
						clearInterval(phoneStateInterval.current);
					}
					phoneStateInterval.current = setInterval(monitorPhoneState, 500);
				} else {
					console.error('SIP account not available');
				}
			} else {
				console.error('Invalid phone number:', numberToUse);
			}
		} catch (error) {
			console.error('Error making call:', error);
			setCallTriggered(false);
		}
	}, [callTriggered, fullPhoneNumber, user, onClose, noBalance, monitorPhoneState]);

	const clearText = useCallback(() => {
		try {
			if (fullPhoneNumber.length <= 1) {
				setFullPhoneNumber("+");
				setCountry(null);
				cursor.current = 1;
			} else {
				// Remove last character
				let newNumber = fullPhoneNumber.substring(0, fullPhoneNumber.length - 1);

				if (newNumber.length <= 1) {
					newNumber = "+";
					setCountry(null);
					cursor.current = 1;
				} else {
					// Check if country should be updated after removal
					const foundCountry = findCountryByDialCode(newNumber);
					if (foundCountry !== country) {
						setCountry(foundCountry);
					}
					cursor.current = newNumber.length;
				}

				setFullPhoneNumber(newNumber);
			}
		} catch (error) {
			console.error('Error clearing text:', error);
		}
	}, [fullPhoneNumber, country, findCountryByDialCode]);

	// Initialize component with caller info or phone number from link
	useEffect(() => {
		console.log('callerInfo', callerInfo)
		try {
			if (callerInfo) {
				const { toNumber } = callerInfo;
				let number = toNumber.indexOf("+") !== -1 ? toNumber : "+" + toNumber;
				number = formatPhoneNumber(null, number);
				const foundCountry = findCountryByDialCode(number);
				number = number.replace(/\s/g, '');
				if (foundCountry) {
					setCountry(foundCountry);
				}
				setShowNumberPlate(false);
				setFullPhoneNumber(number);
				setIsInitialized(true);
				// Pass the number directly to makeCall instead of relying on state
				setTimeout(() => {
					console.log('About to make auto-call with number:', number);
					makeCall(number); // Pass the number directly
				}, 1200);
				const now = Date.now();
				dispatch(getLoggedInUserCallHistory(now));
			} else if (phoneNumFromLink) {
				console.log('phoneNumFromLink', phoneNumFromLink);
				const toNumber = phoneNumFromLink;
				let number = toNumber.indexOf("+") !== -1 ? toNumber : "+" + toNumber;
				number = formatPhoneNumber(null, number);
				const foundCountry = findCountryByDialCode(number);

				if (foundCountry) {
					setCountry(foundCountry);
				}

				if (number) {
					setTimeout(() => {
						setFullPhoneNumber(number);
						setIsInitialized(true);
					}, 200);
				}
			} else {
				setIsInitialized(true);
			}
		} catch (error) {
			console.error('Error in initialization useEffect:', error);
			setIsInitialized(true);
		}
	}, []); // Removed makeCall from dependencies to prevent infinite loop

	// Handle cursor positioning and UI updates
useEffect(() => {
  if (!isInitialized) return;

  setCursorPositions();

  if (phoneState !== "Establishing") return;

  let attempts = 0;

  const interval = setInterval(() => {
    try {
      const root = document.querySelector(".react-sip-phone");
      if (!root) return;

      // Get all images inside SIP phone
      const images = root.querySelectorAll("button img, div img");

      if (images.length >= 3) {
        // Assign safely by index
        if (images[0]) images[0].src = "/img/call_end-24px.svg";
        if (images[1]) images[1].src = "/img/phone_paused-24px.svg";
        if (images[2]) images[2].src = "/img/mic_off-24px.svg";

        clearInterval(interval);
      }

      attempts++;
      if (attempts > 30) {
        clearInterval(interval); // stop after ~3 seconds
      }
    } catch (error) {
      console.warn("Error updating UI elements:", error);
      clearInterval(interval);
    }
  }, 100);

  return () => clearInterval(interval);
}, [phoneState, isInitialized, setCursorPositions]);


	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (phoneStateInterval.current) {
				clearInterval(phoneStateInterval.current);
			}
		};
	}, []);

	const userDomain = selectedDomain || "";

	if (!isInitialized) {
		return <div>Loading...</div>;
	}

	return (
		<div style={{ borderRadius: "0.3rem", overflow: "hidden" }}>
			<div style={{ backgroundColor: "#2FC76F" }}>
				<div className="flex justify-center pt-8">
					<CountrySelector
						onChange={selectCountry}
						selectedCountry={country}
						className=""
						style={{
							background: "transparent",
							border: "none",
							color: "#fff",
							webkitAppearance: "auto",
						}}
					/>
				</div>

				<div className="p-5">
					<div style={{ textAlign: "center" }}>
						<input
							name="value"
							type="tel"
							className="hidden-input"
							style={{
								fontSize: "30px",
								lineHeight: "36px",
								color: "#fff",
								textAlign: "center",
							}}
							value={fullPhoneNumber}
							onChange={handlePhoneNumberChange}
							onKeyDown={handleKeyDown}
							onBlur={() => {
								if (!callerInfo) {
									try {
										const formatted = formatPhoneNumber(null, fullPhoneNumber);
										setFullPhoneNumber(formatted);
									} catch (error) {
										console.error('Error formatting phone number on blur:', error);
									}
								}
							}}
							maxLength="20"
							autoFocus
							ref={phnInput}
							placeholder="Enter phone number"
						/>
					</div>
				</div>
			</div>

			<div className={phoneState === null ? "hidden" : "block react-sip-phone"}>
				<ReactSipPhone
					name={name || ""}
					sipCredentials={{
						sipuri: sipuri || "",
						password: password || "",
					}}
					sipConfig={{
						websocket: websocket || "",
						defaultCountryCode: "",
					}}
					phoneConfig={{
						disabledButtons: disabledButtons || [],
						disabledFeatures: disabledFeatures || [],
						defaultDial: "",
						sessionsLimit: 3,
						attendedTransferLimit: 2,
						autoAnswer: false,
					}}
					appConfig={{
						mode: mode || "",
						started: false,
						appSize: "large",
					}}
					width={0}
				/>
			</div>

			{showNumberPlate && (
				<div
					className={`pad ${userDomain}_font`}
					style={{
						display: phoneState === "Terminated" ? "none" : "block",
					}}
				>
					<NumberPad onClick={addNumber} />

					{phoneState === null && (
						<div className="flex justify-between items-center my-3 px-4">
							<a onClick={onClose} className={`primary-link cursor-pointer ${userDomain}_font`}>
								Close
							</a>
							<a
								className={`flex justify-center items-center btn btn-success btn-icon-o btn-sm btn-rounded mb-2 ${userDomain}_font`}
								style={{
									height: "60px",
									width: "60px",
								}}
								onClick={() => {
									if (reduxState?.balance <= 0) {
										openRecharge()
										onClose()
										showToast('error', `Insufficient balance.`)
									} else {
										makeCall()
									}
								}} // Fixed: Use arrow function instead of calling makeCall immediately
							>
								<img
									src="/img/call-receivecall-btn@2x.png"
									className="w-[40px] h-[40px]"
									alt="call-receivecall-btn@2x.png"
								/>
							</a>
							<a onClick={clearText} className={`secodary-link cursor-pointer ${userDomain}_font`}>
								Clear
							</a>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DiallerKeyPad;